<template>
  <template v-if="tag === 'nuxt-link'">
    <NuxtLink
      :to="to"
      class="new-o-button focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      :class="classes.button"
    >
      <span class="flex items-center o-body justify-center" :class="classes.body">
        <span v-if="iconLeft" class="pr-1">
          <OSvg class="w-2 fill-current icon-left" :svg="iconLeft" />
        </span>
        <span>
          <slot />
        </span>
        <span v-if="iconRight" class="pl-1">
          <OSvg class="w-2 fill-current icon-right" :svg="iconRight" />
        </span>
      </span>
    </NuxtLink>
  </template>
  <component
    :is="enhancedTag"
    v-else
    :type="componentType"
    v-bind="buttonProps"
    class="new-o-button focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    tabindex="0"
    :class="classes.button"
    :disabled="disabled"
    @click="emit('click')"
  >
    <span class="flex items-center o-body justify-center" :class="classes.body">
      <span v-if="iconLeft" class="pr-1">
        <OSvg class="w-2 fill-current icon-left" :svg="iconLeft" />
      </span>
      <span>
        <slot />
      </span>
      <span v-if="iconRight" class="pl-1">
        <OSvg class="w-2 fill-current icon-right" :svg="iconRight" />
      </span>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import OSvg from '@/components/OSvg'

type TagType = 'button' | 'a' | 'nuxt-link'
type ButtonType = 'button' | 'submit'
type VariantType = 'solid' | 'outline' | 'plane' | 'link'
type ColorType = 'green' | 'yellow' | 'blue' | 'white' | 'red' | 'women' | 'black' | 'women-fill' | 'women-orange' | 'dark' | 'dark-green' | 'dark-disabled'
type SizeType = 'md' | 'lg'

interface Attribute {
  name: string
  value: string
}

interface Props {
  tag?: TagType
  type?: ButtonType | null
  disabled?: boolean
  to?: string | object
  href?: string
  iconRight?: string | null
  iconLeft?: string | null
  iconClasses?: string
  variant?: VariantType
  color?: ColorType
  size?: SizeType
  attribute?: Attribute | null
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'button',
  type: null,
  disabled: false,
  to: undefined,
  href: '',
  iconRight: null,
  iconLeft: null,
  iconClasses: '',
  variant: 'solid',
  color: 'yellow',
  size: 'md',
  attribute: null
})

const emit = defineEmits<{
  click: []
}>()

const classes = computed(() => {
  return {
    button: `new-o-button-${props.variant} new-o-button-${props.color} ${props.disabled ? 'new-o-button-disabled' : ''}`,
    body: `new-o-button-body new-o-button-${props.variant}-p new-o-button-${props.size}`
  }
})

const buttonProps = computed(() => {
  const buttonProps: Record<string, any> = {
    ...(props.attribute ? { [props.attribute.name]: props.attribute.value } : {}),
    ...(props.href ? { href: props.href } : {}),
    ...(props.to ? { to: props.to } : {})
  }

  return buttonProps
})

const enhancedTag = computed(() => {
  if (props.tag === 'nuxt-link') {
    return 'NuxtLink'
  }

  return props.tag
})

const componentType = computed(() => {
  if (props.type) {
    return props.type
  }

  if (enhancedTag.value !== 'button') {
    return undefined
  }

  return 'button'
})
</script>

<style lang="postcss" scoped>
.new-o-button {
  @apply inline-block;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  cursor: pointer;
  outline: none !important;
  border-radius: 10rem;
  position: relative;

  &:focus {
    outline: none !important;
  }

  .icon-left,
  .icon-right {
    padding-top: 0.2rem;
  }

  &-body {
    @apply px-5;
    padding-top: 0.95rem;
    padding-bottom: 0.95rem;

    &.new-o-button-lg {
      @apply py-3 px-5;
    }
  }

  &-plane-p {
    @apply px-0;
  }

  &-link-p {
    @apply p-0;
  }

  &-solid {
    @apply font-bold transition-colors ease-in-out duration-300;

    &.new-o-button-disabled {
      @apply opacity-50 cursor-default;
    }

    &.new-o-button-blue {
      @apply bg-newBlue-400 text-neutral-00;

      &:hover {
        @apply bg-newBlue-600;
      }
    }

    &.new-o-button-yellow {
      @apply bg-yellow-400 text-neutral-800;

      &:hover {
        @apply bg-yellow-600;
      }

      span {
        @apply font-bold;
      }
    }

    &.new-o-button-white {
      @apply bg-neutral-00 text-neutral-800;

      &:hover {
        @apply bg-neutral-50;
      }
    }

    &.new-o-button-red {
      @apply bg-newRed-400 text-neutral-00;

      &:hover {
        @apply bg-newRed-600;
      }
    }

    &.new-o-button-women {
      @apply bg-women-text text-women-shade;

      &:hover {
        @apply bg-women-textShade;
      }
    }

    &.new-o-button-green {
      @apply bg-green-400 text-neutral-00;

      &:hover {
        @apply bg-green-500;
      }
    }

    &.new-o-button-black {
      @apply bg-neutral-800 text-neutral-00;

      &:hover {
        @apply bg-black;
      }
    }

    &.new-o-button-women-fill {
      @apply bg-women-fill text-neutral-00;

      &:hover {
        @apply bg-women-fill-hover;
      }
    }

    &.new-o-button-women-orange {
      @apply text-neutral-00;
    }

    &.new-o-button-dark {
      @apply bg-black text-neutral-00;

      &:hover {
        @apply bg-black;
      }

      &.new-o-button-disabled,
      &.new-o-button-disabled:hover {
        @apply opacity-25;
      }
    }

    &.new-o-button-dark-green {
      @apply bg-green-400 text-neutral-00;

      &:hover {
        @apply bg-green-500;
      }
    }

    &.new-o-button-dark-disabled {
      @apply bg-black text-neutral-00 opacity-25 cursor-pointer;
    }
  }

  &-link {
    @apply underline;
    border-radius: 0;

    &:hover {
      @apply no-underline;
    }
  }

  &-outline {
    @apply font-semibold border-1 border-solid;

    &:hover {
      @apply bg-neutral-50;
    }

    &.new-o-button-blue {
      @apply border-newBlue-400 text-newBlue-400;
    }

    &.new-o-button-dark {
      @apply border-black text-black;

      &.new-o-button-disabled,
      &.new-o-button-disabled:hover {
        @apply opacity-25;
      }
    }
  }
}

.new-o-button:focus {
  --ring-color: #36c0b6;
  --ring-offset-width: 2px;
  --ring-offset-color: transparent;
  --ring-width: 2px;
  --ring-padding: 0px;
  box-shadow: 0 0 0 var(--ring-offset-width) var(--ring-offset-color),
    0 0 0 calc(var(--ring-offset-width) + var(--ring-width) + var(--ring-padding)) var(--ring-color) !important;
}
</style>
